/**
 * Guest-facing names for the three API payment methods, keyed by fulfilment.
 *
 * Port of iOS `PaymentMethodDisplay.swift`. "Card" sitting next to "Online payment" is
 * how a guest pays twice, or not at all: both read as "I pay with a card", and only one
 * of them charges them right now. The wording here is the whole fix — each row says
 * where and when the money actually moves ("Card at the table", "Pay by card at the
 * table"), so the choice is legible without the guest having to infer it.
 *
 * Keys rather than strings, because the caller renders them through ngx-translate.
 */

/** API spellings. Identical on iOS (`PaymentMethod.swift`) and Android. */
export const PAYMENT_METHOD_CASH = 'cash_on_delivery';
export const PAYMENT_METHOD_CARD = 'card_on_delivery';
export const PAYMENT_METHOD_ONLINE = 'online_payment';

/** Where the guest will complete a pay-later method, or sit for dine-in. */
export type PaymentFulfilment = 'delivery' | 'pickup' | 'dineIn';

/** Title and subtitle shown on a payment-method row, as i18n keys. */
export interface PaymentMethodCopy {
  titleKey: string;
  /** Empty when there is no sentence to add — the row then renders title only. */
  subtitleKey: string;
}

const OPTIONS_PREFIX = 'orderDetails.paymentMethod.options';

/**
 * Cash and card are worded for WHERE the guest will be when they pay, so the two lists
 * are keyed on fulfilment. Online payment is not: it charges them in the browser, in
 * the next breath, wherever the food is going.
 */
const COPY_BY_FULFILMENT: Record<PaymentFulfilment, Record<string, string>> = {
  pickup: {
    [PAYMENT_METHOD_CASH]: 'cashPickup',
    [PAYMENT_METHOD_CARD]: 'cardPickup',
  },
  delivery: {
    [PAYMENT_METHOD_CASH]: 'cashDelivery',
    [PAYMENT_METHOD_CARD]: 'cardDelivery',
  },
  dineIn: {
    [PAYMENT_METHOD_CASH]: 'cashDineIn',
    [PAYMENT_METHOD_CARD]: 'cardDineIn',
  },
};

/**
 * The copy for one method.
 *
 * ⚠️ An unrecognised method falls back to the RAW API STRING, not to a method name.
 * ngx-translate fails soft, so `titleKey: method` renders as `some_new_method` on
 * screen — which is the point. It should look wrong. Silently labelling an unknown
 * value "Cash" is how a guest is shown one thing and the venue is told another, and
 * the venue is the one who finds out at the till.
 */
export function paymentMethodCopy(method: string, fulfilment: PaymentFulfilment): PaymentMethodCopy {
  if (method === PAYMENT_METHOD_ONLINE) {
    return {
      titleKey: `${OPTIONS_PREFIX}.online.title`,
      subtitleKey: `${OPTIONS_PREFIX}.online.subtitle`,
    };
  }

  const name = COPY_BY_FULFILMENT[fulfilment]?.[method];
  if (!name) {
    return { titleKey: method, subtitleKey: '' };
  }

  return {
    titleKey: `${OPTIONS_PREFIX}.${name}.title`,
    subtitleKey: `${OPTIONS_PREFIX}.${name}.subtitle`,
  };
}

/**
 * What the committing button says once a method is chosen.
 *
 * Online payment gets "Continue to payment" because the next tap does not finish the
 * order — it hands the guest to BORICA's card form, and telling them they have ordered
 * at that moment is a lie they only discover if the payment then fails.
 */
export function commitLabelKey(method: string): string {
  return method === PAYMENT_METHOD_ONLINE
    ? 'orderDetails.orderNow.continueToPayment'
    : 'orderDetails.orderNow.button';
}
